import { ProductSkuDTO } from "@touche-lts/panel-ui/dist"
import { DropdownSelectOption } from "@touche-lts/panel-ui/dist/components/shared/dropdown-select"
import { AttributeGroupType, AttributeGroupValueDTO } from "@touche-lts/panel-ui/dist"
import { ImageIndications, RemoveCroppingFunc } from 'components/shared'
import { getImageIndications } from 'components/shared/ImageEditable/ImageEditable.utils'
import { removeCroppingTool } from 'components/shared/Picture/SettingIndicator'
import { action, computed, observable } from 'mobx'
import { computedFn } from "mobx-utils"
import { AspectTypes, AttributeGroupN, GalleryImage, MediaContentDTO, Product, Sku } from 'models'
import { PRODUCT_SKU_STORE } from "store/ProductSkuStore"
import { PRODUCT_STORE } from "store/ProductStore"
import { RootStore } from "store/RootStore"
import { getToFixed2Number } from 'utilities'
import { aspectRatioValues, CroppedAreaPixels, CropValues, defaultCropValues, ImageGalleryEditorInitProps } from './ImageEditorStore.types'

export const IMAGE_EDITOR_STORE = 'IMAGE_EDITOR_STORE'

export class ImageEditorStore {
  rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  private onPopupClose: ImageGalleryEditorInitProps['onClose']
  private setImagesOnDone: ImageGalleryEditorInitProps['setImages']
  private setSkusOnDone: (skus: Sku[]) => void

  // OBSERVABLES
  @observable public images: GalleryImage[] = []
  @observable public skus: Sku[] = []
  @observable public indexOnFocus: number = 0
  @observable public selectedTab: AspectTypes = AspectTypes.PORTRAIT
  @observable public zoomLevel: number = 1
  @observable public cropValues: CropValues = defaultCropValues
  @observable public initialCroppedAreaPixels: CroppedAreaPixels = null
  @observable private attributeGroups: Map<number, AttributeGroupN>
  @observable public imageAttributeValuesMap: Map<number, Set<number>> = new Map()
  @observable public currentProductId: number

  // ACTIONS

  @action public addAttributeValueToImage = (attributeValueId: number): void => {
    this.imageAttributeValuesMap.get(this.selectedImage.id).add(attributeValueId)
  }

  @action public removeAttributeValueFromImage = (attributeValueId: number): void => {
    this.imageAttributeValuesMap.get(this.selectedImage.id).delete(attributeValueId)
  }

  public getProductSkus = (): Sku[] => {
    const { productToView} = this.rootStore[PRODUCT_STORE]

    if (!productToView.productSkus) return null
    productToView.productSkus.forEach(sku => {
      const skuImages: MediaContentDTO[] = []
      const skuAttributeValueIds = sku.attributes.map(attr => attr.id)
      this.imageAttributeValuesMap.forEach((attributeValueIdsSet, imageId) => {
        const imageAttributeValueIdsGroupedByAttributeGroup: number[][] = []
        this.attributeGroupIds.forEach(attributeGroupId => {
          const attributeValueIdsByGroup = this.getAttributeValuesByGroup(
            attributeGroupId
          ).map(attributeValue => attributeValue.id)
          const imageAttributeValueIdsFromThisGroup = attributeValueIdsByGroup.filter(
            id => attributeValueIdsSet.has(id)
          )
          if (imageAttributeValueIdsFromThisGroup.length) {
            imageAttributeValueIdsGroupedByAttributeGroup.push(
              imageAttributeValueIdsFromThisGroup
            )
          }
        })

        const skuAttributeValuesCorrespondToAllImageAttributeValues =
          imageAttributeValueIdsGroupedByAttributeGroup.length &&
          !imageAttributeValueIdsGroupedByAttributeGroup.some(idsGroup => {
            return !idsGroup.some(id => skuAttributeValueIds.includes(id))
          })
        if (skuAttributeValuesCorrespondToAllImageAttributeValues) {
          const currentImgObject = productToView.images.find(img => img.id === imageId)
          skuImages.push(currentImgObject as MediaContentDTO)
        }
      })
      sku.images = skuImages
    })

    return productToView.productSkus
  }
  
  @observable public getAttributeGroupSelectorProps = computedFn(
    (
      attributeGroupId: number
    ): {
      attributeGroupId: number
      groupLabel: string
      selectOptions: DropdownSelectOption[]
    } => {

      return {
        attributeGroupId,
        groupLabel: this.getAttributeGroupLabel(attributeGroupId),
        selectOptions: this.getAttributeValuesByGroup(attributeGroupId).map(
          attributeValue => {
            return {
              value: attributeValue.id,
              label: attributeValue.name,
              isSelected: this.isAttributeValueSelected(attributeValue.id),
              colorHex: this.isAttributeGroupTypeColor(attributeGroupId)
                ? attributeValue.value
                : undefined
            }
          }
        )
      }
    }
  )

  public onAttributeValueClick = (attributeValueId: number): void => {

    this.isAttributeValueSelected(attributeValueId)
      ? this.removeAttributeValueFromImage(attributeValueId)
      : this.addAttributeValueToImage(attributeValueId)
  }

  @action private getAttributeGroupById = (
    attributeGroupId: number
  ): AttributeGroupN => {
    return this.attributeGroups.get(attributeGroupId)
  }

  @action private getAttributeValuesByGroup = (
    attributeGroupId: number
  ): AttributeGroupValueDTO[] | any => {
    const attributeGroup = this.getAttributeGroupById(attributeGroupId)
    return attributeGroup?.values || []
  }

  @action private  getAttributeGroupLabel = (attributeGroupId: number): string => {
    const attributeGroup = this.getAttributeGroupById(attributeGroupId)
    return attributeGroup?.name || ''
  }

   private isAttributeValueSelected = computedFn((attributeValueId: number): boolean => {
    return Boolean(
      this.imageAttributeValuesMap
        .get(this.selectedImage.id)
        ?.has(attributeValueId)
    )
  })

  @action private isAttributeGroupTypeColor = (attributeGroupId: number): boolean => {
    const attributeGroup = this.getAttributeGroupById(attributeGroupId)
    return attributeGroup?.type === AttributeGroupType.COLOR
  }

   public feedSkuStoreOnMount = (
    attributeGroups: AttributeGroupN[],
    imageIds: number[],
    productSkus: Sku[]
  ): void => {
    const { productToView } = this.rootStore[PRODUCT_STORE]
    if (!productToView.id) {
      this.attributeGroups = null;
    }

    if(productToView.id){
    this.attributeGroups = new Map(
      attributeGroups.map(attributeGroup => [
        attributeGroup.id,
        attributeGroup
      ])
    )


    const imageAttributeValuesMap: Map<number, Set<number>> = new Map()
    imageIds.forEach(imageId => {
      const imageAttributeValueIdsSet: Set<number> = new Set()

      const skusImageIsRelatedTo = productSkus.filter(sku => {
        return sku.images.some(img => img.id === imageId)
      })

      skusImageIsRelatedTo.forEach(sku => {
        sku.attributes.forEach(attribute => {
          imageAttributeValueIdsSet.add(attribute.id)
        })
      })

      imageAttributeValuesMap.set(imageId, imageAttributeValueIdsSet)
    })
    this.setImageAttributeValuesMap(imageAttributeValuesMap)
  }
  }

  @action public setImageAttributeValuesMap = (
    imageAttributeValuesMap: Map<number, Set<number>>
  ): void => {
    this.imageAttributeValuesMap = imageAttributeValuesMap
  }

  @computed public get attributeGroupIds (): number[] {

    if (!this.attributeGroups) return []
    return Array.from(this.attributeGroups.keys())
  }

  @action private setImages = (images: GalleryImage[]): void => {
    this.images = images
  }

  @action private setImageProps = (imageProps: Partial<GalleryImage>): void => {
    this.images[this.indexOnFocus] = {
      ...this.images[this.indexOnFocus],
      ...imageProps
    }
  }

  @action public setSkus = (skus: Sku[]): void => {
    this.skus = skus
  }

  @action private setIndexOnFocus = (index: number): void => {
    this.indexOnFocus = index
  }

  @action private setSelectedTab = (tab: AspectTypes): void => {
    this.selectedTab = tab
  }

  @action public setZoomLevel = (zoomLevel: number): void => {
    this.zoomLevel = zoomLevel
  }

  @action private setCropValues = (values: CropValues): void => {
    this.cropValues = values
  }

  @action private setInitialCroppedAreaPixels = (pixels: CroppedAreaPixels): void => {
    this.initialCroppedAreaPixels = pixels
  }

  // METHODS
  public onImageGalleryEditorMount = (props: ImageGalleryEditorInitProps): void => {
    const {
      images,
      indexOnFocus,
      additionalProps,
      onClose,
      setImages
    } = props

    this.setImages(images)
    this.setIndexOnFocus(indexOnFocus)
    this.setSkus(additionalProps?.skus || [])
    this.setSkusOnDone = additionalProps?.setSkus
    this.onPopupClose = onClose
    this.setImagesOnDone = setImages

    this.setInitialCropAndZoomValuesForSelectedPicture()

    const { productToView } = this.rootStore[PRODUCT_STORE]
    const attGroups = productToView.attGroups;
    const skus = productToView.productSkus;
    const imgIds: number[] = images.map(img => img.id);
    this.feedSkuStoreOnMount(attGroups, imgIds, skus)

  }

  public onSelectedTabChange = (tab: AspectTypes): void => {
    this.setSelectedTab(tab)
    this.setInitialCropAndZoomValuesForSelectedPicture()
  }

  public onRemoveCroppingClick = (index: number): RemoveCroppingFunc => {
    return (croppingToRemove): void => {
      removeCroppingTool(
        croppingToRemove,
        index,
        this.images,
        this.setImages
      )
    }
  }

  public onImageOnFocusRemoveCroppingClick = (): void => {
    this.onRemoveCroppingClick(this.indexOnFocus)(this.aspect)
    this.setInitialCropAndZoomValuesForSelectedPicture()
  }

  public onApplyCroppingAndProceedClick = (): void => {
    if (this.aspect === AspectTypes.PORTRAIT) {
      this.onSelectedTabChange(AspectTypes.SQUARE)
    } else {
      const isLastImageSelected = this.indexOnFocus + 1 === this.images.length
      const nextIndex = isLastImageSelected ? 0 : this.indexOnFocus + 1
      this.setSelectedTab(AspectTypes.PORTRAIT)
      this.onIndexOnFocusChange(nextIndex)
    }
  }

  public onGeneralUseCheckboxClick = (): void => {
    this.setImageProps({
      [this.aspect]: !this.isSelectedImageAspectCropForGeneralUse
    })
  }
  
  private setInitialCropAndZoomValuesForSelectedPicture = (): void => {
    if (!this.selectedImage) return

    const {
      originalHeight,
      originalWidth
    } = this.selectedImage

    const aspectWidth: number = this.selectedImage[`${this.aspect}Width`] || 1
    const aspectX: number = this.selectedImage[`${this.aspect}X`] || 0
    const aspectY: number = this.selectedImage[`${this.aspect}Y`] || 0

    const width = originalWidth / aspectWidth
    const height = width / this.aspectRatio

    const initialZoomLevel = getToFixed2Number(originalHeight / height)
    const initialCroppedAreaPixels: CroppedAreaPixels = {
      width,
      height,
      x: aspectX / 100 * originalWidth,
      y: aspectY / 100 * originalHeight
    }

    this.setZoomLevel(initialZoomLevel)
    this.setCropValues({ x: aspectX, y: aspectY })
    this.setInitialCroppedAreaPixels(initialCroppedAreaPixels)
  }

  public onIndexOnFocusChange = (index: number): void => {
    this.setIndexOnFocus(index)
    this.setInitialCropAndZoomValuesForSelectedPicture()
  }

  public onCropChange = (crop: CropValues): void => {
    this.setCropValues(crop)
  }

  public onCropComplete = (croppedArea: CropValues, croppedAreaPixels: CroppedAreaPixels): void => {
    const { x, y } = croppedArea
    const { width: newWidth } = croppedAreaPixels

    const newImageProps: Partial<GalleryImage> = {
      [`${this.aspect}Width`]: this.selectedImage?.originalWidth / newWidth,
      [`${this.aspect}X`]: x,
      [`${this.aspect}Y`]: y
    }

    this.setImageProps(newImageProps)
  }

  public onDoneClick = (): void => {

    this.setImagesOnDone && this.setImagesOnDone(this.images)

    const { setProductToView, productToView } = this.rootStore[PRODUCT_STORE]
    
    setProductToView(new Product({
      ...productToView,
      productSkus: this.getProductSkus(),
    }))

      if (this.skus && this.setSkusOnDone) {
        this.setSkusOnDone([...this.skus])
      }

    this.onCloseClick()
  }

  public onCloseClick = (): void => {
    this.onPopupClose && this.onPopupClose()
    this.clearStore()
  }

  private clearStore = (): void => {
    // this.setImages([])
    this.setIndexOnFocus(0)
    this.setSkus([])
    this.setSelectedTab(AspectTypes.PORTRAIT)
    this.setSkusOnDone = undefined
    this.onPopupClose = undefined
    this.setImagesOnDone = undefined

    this.setZoomLevel(1)
    this.setCropValues(defaultCropValues)
    this.setInitialCroppedAreaPixels(null)
  }

  // COMPUTED OBSERVABLES
  @computed public get selectedImage (): GalleryImage {
    try {
      if (!this.images || !this.images.length) return null
      return this.images[this.indexOnFocus]
    } catch (error) {
      console.error(error)
      return null
    }
  }

  @computed public get selectedImageSrc (): string | ArrayBuffer {
    const result = this.selectedImage?.url || this.selectedImage?.src || ''
    if (typeof result === 'string') {
      return encodeURI(result)
    } else {
      return result
    }
  }

  @computed public get aspect (): AspectTypes {
    return this.selectedTab
  }

  @computed public get aspectRatio (): number {
    return aspectRatioValues[this.aspect]
  }

  @computed public get shouldRenderCropTool (): boolean {
    return Boolean(this.selectedImageSrc && this.initialCroppedAreaPixels)
  }

  @computed private get selectedImageIndications (): ImageIndications {
    if (!this.selectedImage) return null
    return getImageIndications(this.selectedImage)
  }

  @computed public get isSelectedImageAspectCropApplied (): boolean {
    return Boolean(this.selectedImageIndications?.[`${this.aspect}Crop`])
  }

  @computed public get isSelectedImageAspectCropForGeneralUse (): boolean {
    return Boolean(this.selectedImageIndications?.[`${this.aspect}ForGeneralUse`])
  }
}
